import { useHandleOAuthRedirectMutation } from '@/features/auth/authApi';
import { getStorageItem, removeStorageItem } from '@/shared/helpers/storage';
import { useWindowDimensions } from '@/shared/hooks/useWindowDimension';
import { COOKIE_OAUTH_STATE_KEY, TOKEN_COOKIE_NAMES } from '@/shared/utils/constants';
import { camelizeNestedKeys } from '@cex/shared-modules';
import { useIonRouter, useIonToast } from '@ionic/react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { setOAuth } from '@/features/oAuth/oAuthSlice';

const oauthQueryParams = ['code', 'state', 'user_specific_client_id', 'user_specific_client_secret'];

const OAuth: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [presentToast] = useIonToast();
  const { isMobileView } = useWindowDimensions();
  const { t } = useTranslation();
  const router = useIonRouter();

  const [handleOAuthRedirect, { isLoading, isSuccess }] = useHandleOAuthRedirectMutation();

  const oAuthRedirect = useCallback(async () => {
    try {
      const urlSearchParams = new URLSearchParams(location.search);
      const params = oauthQueryParams.reduce(
        (acc, key) => ({
          ...acc,
          [key]: urlSearchParams.get(key)
        }),
        {}
      );

      const { code, state, userSpecificClientId, userSpecificClientSecret } = camelizeNestedKeys(params);

      getStorageItem(COOKIE_OAUTH_STATE_KEY)
        .then((persistedState) => {
          if (persistedState !== state) {
            throw 'error.generic';
          }
        })
        .catch((error) => {
          presentToast({
            message: t(error),
            duration: 2000,
            icon: 'assets/icons/error-circle-outline.svg',
            position: isMobileView ? 'bottom' : 'top',
            color: 'danger'
          });
          console.error('persisted state does not match the state');
          router.push('/');
        });
      await handleOAuthRedirect({
        code,
        state,
        userSpecificClientId,
        userSpecificClientSecret
      });

      const accessToken = await getStorageItem(TOKEN_COOKIE_NAMES['access']);
      const refreshToken = await getStorageItem(TOKEN_COOKIE_NAMES['refresh']);
      dispatch(setOAuth({ accessToken, refreshToken }));
      router.push('/');
      void removeStorageItem(COOKIE_OAUTH_STATE_KEY);
    } catch (error) {
      console.error(`oAuthRedirect ${error}`);
    }
  }, [handleOAuthRedirect, isMobileView, location.search, router, presentToast, t, dispatch]);

  useEffect(() => {
    if (!isLoading && !isSuccess) {
      void oAuthRedirect();
    }
  }, [isLoading, isSuccess, oAuthRedirect]);

  return <></>;
};

export default OAuth;
